import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { ReactComponent as PlusIcon } from "../../../assets/global-icons/plus.svg";
import BaseSwitch from "../../../components/ui/BaseSwitch";
import { useDispatch, useSelector } from "react-redux";
import {
  addQualification,
  removeQualification,
  updateQualification,
  updateSubQualificationOptions,
  updateWorkOrderFormValidation,
} from "../../../store/workOrder/workOrderSlice";
import { useQuery } from "@tanstack/react-query";
import {
  fetchQualificationTypes,
  fetchSubQualificationTypes,
} from "../../../services/api/qualification";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { useParams } from "react-router-dom";
import { showToast } from "../../../store/toast/toastSlice";

const QualificationForm = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = !!id || props?.isEditMode;

  const qualifications = useSelector((state) => state.workOrder.scheduling.qualifications);
  const workorderstate = useSelector((state) => state.workOrder);
  const workOrderFormValidation = useSelector((state) => state.workOrder.workOrderFormValidation);
  const [isWorkQualification, setIsWorkQualification] = useState(true);
  const [listOfQualifications, setListOfQualifications] = useState([]);

  const subQualificationOptions = useSelector((state) => state.workOrder.subQualificationOptions);

  const {
    isLoading: IsqualificationsTypesLoading,
    error: isqualificationsTypesError,
    data: qualificationsTypesData,
  } = useQuery({
    queryKey: ["qualificationsTypes"],
    queryFn: fetchQualificationTypes,
  });

  const {
    isLoading: IssubQualificationsDataLoading,
    error: issubQualificationsDataError,
    data: subQualificationsData,
  } = useQuery({
    queryKey: ["subQualifications", qualifications?.qualificationTypeId], // Add qualificationTypeId to the queryKey
    queryFn: () => fetchSubQualificationTypes(qualifications?.qualificationTypeId),
    enabled: false,
  });
  useImperativeHandle(ref, () => ({
    isValid: () => {
      const errors = [];

      const fail = (fieldName, message) => {
        errors.push(message);
        dispatch(updateWorkOrderFormValidation({ fieldName, data: true }));
      };

      if (
        !qualifications ||
        qualifications.length === 0 ||
        (qualifications.length === 1 && !qualifications[0].qualificationTypeId)
      ) {
        fail("qualificationTypeId", "At least one qualification is required");
      } else if (
        qualifications.some(
          (qualification) =>
            qualification.qualificationTypeId && !qualification.qualificationSubTypes?.length,
        )
      ) {
        fail("qualificationSubTypes", "Select at least one sub-type for each qualification");
      }

      return { valid: errors.length === 0, errors };
    },
  }));

  const formattedQualificationTypes = qualificationsTypesData?.data?.data?.map(
    (qualificationType) => ({
      label: qualificationType?.name,
      value: qualificationType?.id,
    }),
  );

  const handleAddQualification = () => {
    dispatch(addQualification());
  };

  useEffect(() => {
    if (isEdit) {
      const fetchSubQualificationsData = async () => {
        if (qualifications && qualifications?.length > 0) {
          for (let i = 0; i < qualifications?.length; i++) {
            const qualificationTypeId = qualifications[i]?.qualificationTypeId;

            if (qualificationTypeId) {
              const fetchedSubQualifications =
                await fetchSubQualificationTypes(qualificationTypeId);

              dispatch(
                updateSubQualificationOptions({
                  qualificationTypeId: qualificationTypeId,
                  options: fetchedSubQualifications?.data?.map((subQualification) => ({
                    label: subQualification.name,
                    value: subQualification.id,
                  })),
                }),
              );
            }
          }
        }
      };

      fetchSubQualificationsData();
    }
  }, [qualifications]);

  const handleChange = async (index, event) => {
    const { name, value } = event.target;

    // If the changed field is qualificationTypeId, fetch sub-qualifications
    if (name === "qualificationTypeId") {
      const fetchedSubQualifications = await fetchSubQualificationTypes(value);

      // Update sub-qualification options for the selected qualificationTypeId
      dispatch(
        updateSubQualificationOptions({
          qualificationTypeId: value,
          options: fetchedSubQualifications.data.map((subQualification) => ({
            label: subQualification.name,
            value: subQualification.id,
          })),
        }),
      );
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "qualificationTypeId",
          data: false,
        }),
      );
    }
    dispatch(updateQualification({ index, field: name, value: value }));
    dispatch(
      updateWorkOrderFormValidation({
        fieldName: "qualificationSubTypes",
        data: false,
      }),
    );
  };

  const removeItem = (index) => {
    if (qualifications.length === 1) {
      dispatch(
        showToast({
          message: "At least one qualification is required.",
          type: "error",
        }),
      );
      return;
    }

    dispatch(removeQualification(index));
  };

  return (
    <>
      <BaseLoader isLoading={IsqualificationsTypesLoading || IssubQualificationsDataLoading} />

      <BaseCard className="py-4 lg:px-16 px-5 mt-2 rounded-lg" elevation={1}>
        <div className="border-bottom mb-4 flex items-center">
          <h6 className="mb-2 text-dark">Work Order Eligibility </h6>
          {/* <p>(Optional)</p> */}
          {/* <BaseSwitch
            className="ml-4 mb-2"
            secondary={true}
            checked={isWorkQualification}
            onChange={() => setIsWorkQualification(!isWorkQualification)}
          /> */}
        </div>

        {isWorkQualification && (
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <p className="leading-normal mb-4">
                Qualifications may encompass various elements such as licenses, certifications,
                insurance, equipment, screenings, and specific selection criteria. If any
                qualifications are absent from a requesting provider's profile, you will be prompted
                to confirm the assignment.
              </p>
            </div>

            {qualifications?.map((qualification, index) => (
              <div
                key={`${qualification.id}-${index}`}
                className="col-span-2 grid grid-cols-2 xl:px-5 px-3 py-2 rounded-lg mb-4"
                style={{ backgroundColor: "#F6F6F6" }}
              >
                <div className="col-span-2 grid grid-cols-12 lg:items-center">
                  <div className="col-span-1  cursor-pointer " onClick={() => removeItem(index)}>
                    {/* <RemoveIcon /> */}
                    <i class="fa-solid fa-minus bg-secondary text-white rounded-full py-[1px] px-[2px]"></i>
                  </div>
                  <div className="col-span-11 grid grid-cols-2 gap-x-6">
                    <div className="w-full lg:mr-10 lg:col-span-1 col-span-2">
                      <BaseAutoSelect
                        placeholder="Select Qualification Type"
                        className="mb-2"
                        fullHeight={false}
                        multiple={false}
                        requirement="required"
                        labelShrink={false}
                        label="Qualification Type"
                        options={formattedQualificationTypes}
                        name="qualificationTypeId"
                        isDefaultField={false}
                        value={qualification.qualificationTypeId}
                        onChange={(e, newValue) => {
                          const customEvent = {
                            target: {
                              name: "qualificationTypeId",
                              value: e.target.value,
                            },
                          };
                          handleChange(index, customEvent);
                        }}
                        error={workOrderFormValidation?.qualificationTypeId}
                        errorMsg="Qualification Type is required"
                      />
                    </div>
                    <div className="w-full lg:col-span-1 col-span-2">
                      <BaseAutoSelect
                        className="mb-2"
                        labelShrink={false}
                        label={
                          formattedQualificationTypes?.find(
                            (type) => type.value === qualification?.qualificationTypeId,
                          )?.label || "License"
                        }
                        fullHeight={false}
                        placeholder="Search..."
                        isDefaultField={false}
                        requirement="required"
                        options={subQualificationOptions[qualification.qualificationTypeId] || []} // Use the dynamically fetched options
                        disableCloseOnSelect
                        name="qualificationSubTypes"
                        multiple
                        value={qualification.qualificationSubTypes}
                        onChange={(e) => {
                          const customEvent = {
                            target: {
                              name: "qualificationSubTypes",
                              value: e.target.value,
                            },
                          };
                          handleChange(index, customEvent);
                        }}
                        error={workOrderFormValidation?.qualificationSubTypes}
                        errorMsg="Sub-Qualification Type is required"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="flex items-center mt-2 col-span-2">
              <PlusIcon className="cursor-pointer" onClick={handleAddQualification} />

              <p className="text-secondary ml-2 cursor-default"> Add Qualification</p>
            </div>
          </div>
        )}
      </BaseCard>
    </>
  );
});

export default QualificationForm;
